import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useHandleLoginClick } from '../../utils/utils';
import './Header.css';
import logo from '/apple-icon-120x120.png';

const Header = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const handleLoginClick = useHandleLoginClick();

  // Check if we're on the root path (landing page) or events selection page
  const isRootPath = location.pathname === '/';
  const isEventsPath = location.pathname === '/events';
  
  // Check if we're on an event route (either /event/:customer/:event or /:customer/:event)
  const isEventRoute = location.pathname.startsWith('/event/') || 
                      (location.pathname.includes('/') && location.pathname !== '/' && location.pathname !== '/events' && location.pathname !== '/login' && location.pathname !== '/register');
  


  const handleOpenDialog = () => {
    setDialogOpen(true);
    setPassword('');
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setPassword('');
    setError('');
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/verify-admin`, { password });
      if (response.data.success) {
        // Navigate directly to events selection
        navigate('/events');
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Admin password verification failed:', error);
      setError('Incorrect password');
    }
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        background: 'linear-gradient(90deg, #000000 40%, #ff5b30 70%, #f9034e 80%, #ef60b5 100%)',
        height: '60px',
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <Toolbar sx={{ height: '100%', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        <Typography 
            variant="h5" 
            component="h4" 
            sx={{ 
              color: 'white',
              fontSize: '1.0rem',
              // fontWeight: 'bold',
              letterSpacing: '0.5px',
              marginRight: 0
            }}
          >
            Studio
          </Typography>
        <img 
            src={logo} 
            alt="CJP Logo" 
            style={{ 
              height: '30px',
              width: 'auto',
              objectFit: 'contain'
            }} 
          />
          <Typography 
            // variant="h4" 
            component="h4" 
            sx={{ 
              fontSize: '0.8rem',
              color: 'white',
              letterSpacing: '0.5px',
              marginLeft: '0.8rem'
            }}
          >
            Beyond amazing
          </Typography>
        </Box>

        {/* Right side controls container */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* nav controls - only show when not on an event route */}
          {!isEventRoute && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                onClick={() => navigate('/')}
                className="global-nav-link"
              >
                <Typography 
                  variant="body" 
                >
                  Home
                </Typography>
                <Box
                  className="underline"
                />
              </Box>
              <Box
                onClick={handleLoginClick}
                className="global-nav-link"
              >
                <Typography 
                  variant="body" 
                >
                  Photos
                </Typography>
                <Box
                  className="underline"
                />
              </Box>
            </Box>
          )}

          {/* Login/Logout buttons - only show when not on an event route */}
          {!isEventRoute && (
            <>
              {!user && (
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/login')}
                  sx={{ 
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.8)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Login
                </Button>
              )}
              {user && (
                <Button 
                  variant="outlined" 
                  onClick={logout}
                  sx={{ 
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.8)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Log out
                </Button>
              )}
            </>
          )}
          
          {/* Gear icon - show when on event route or when not on root/events paths */}
          {(isEventRoute || (!isRootPath && !isEventsPath)) && (
            <IconButton 
              onClick={handleOpenDialog}
              sx={{ 
                color: 'white',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 0.8)'
                }
              }}
            >
              <SettingsIcon />
            </IconButton>
          )}
        </Box> 
      </Toolbar>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
      >
        <DialogTitle>Enter Password to Return to Selection</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Header; 